import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  LayoutDashboard, 
  GanttChart, 
  Kanban, 
  FileSpreadsheet, 
  ArrowRight,
  CheckCircle2,
  Zap
} from 'lucide-react';

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <GanttChart className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-xl text-zinc-900">ProjectOS</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" data-testid="login-nav-btn" className="text-zinc-600 hover:text-zinc-900">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button data-testid="get-started-nav-btn" className="bg-primary hover:bg-orange-600 text-white rounded-lg px-6">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-8">
              <Zap className="w-4 h-4" />
              Project Management Made Simple
            </div>
            <h1 className="font-outfit text-5xl md:text-6xl font-bold text-zinc-900 tracking-tight mb-6">
              Plan, Track, and<br />
              <span className="text-primary">Deliver Projects</span>
            </h1>
            <p className="text-lg text-zinc-600 leading-relaxed mb-10 max-w-2xl mx-auto">
              Import your WBS, visualize with Gantt charts, manage tasks on Kanban boards, 
              and export everything to Excel. All in one powerful platform.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link to="/register">
                <Button 
                  data-testid="hero-get-started-btn" 
                  className="bg-primary hover:bg-orange-600 text-white rounded-lg px-8 py-6 text-lg font-medium transition-all active:scale-95"
                >
                  Start Free <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button 
                  variant="outline" 
                  data-testid="hero-signin-btn"
                  className="border-zinc-200 text-zinc-900 hover:bg-zinc-50 rounded-lg px-8 py-6 text-lg font-medium"
                >
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-zinc-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-outfit text-3xl font-semibold text-zinc-900 mb-4">
              Everything You Need
            </h2>
            <p className="text-zinc-600 max-w-xl mx-auto">
              A complete toolkit for managing projects from start to finish
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-stagger">
            {[
              {
                icon: FileSpreadsheet,
                title: 'WBS Import',
                description: 'Upload CSV or Excel files to quickly import your Work Breakdown Structure'
              },
              {
                icon: GanttChart,
                title: 'Gantt Chart',
                description: 'Interactive timeline with drag-and-drop, task dependencies, and daily view'
              },
              {
                icon: Kanban,
                title: 'Kanban Board',
                description: 'Visual task management with customizable columns and drag-drop cards'
              },
              {
                icon: LayoutDashboard,
                title: 'Excel Export',
                description: 'Export all your project data to Excel format with one click'
              }
            ].map((feature, idx) => (
              <div 
                key={idx}
                className="bg-white p-6 rounded-xl border border-zinc-200 shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-outfit font-semibold text-lg text-zinc-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-zinc-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-outfit text-3xl font-semibold text-zinc-900 mb-6">
                Why Choose ProjectOS?
              </h2>
              <div className="space-y-4">
                {[
                  'Import WBS from CSV or Excel files in seconds',
                  'Visualize task dependencies on interactive Gantt charts',
                  'Manage workflows with customizable Kanban boards',
                  'Track milestones and project progress',
                  'Export everything to Excel with one click'
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-status-success flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-zinc-100 rounded-2xl p-8 aspect-square flex items-center justify-center">
              <img 
                src="https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=600&h=600&fit=crop"
                alt="Project Planning"
                className="rounded-xl shadow-lg w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-zinc-900">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-outfit text-3xl font-semibold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-zinc-400 mb-8">
            Join thousands of project managers who trust ProjectOS
          </p>
          <Link to="/register">
            <Button 
              data-testid="cta-get-started-btn"
              className="bg-primary hover:bg-orange-600 text-white rounded-lg px-8 py-6 text-lg font-medium"
            >
              Create Free Account <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
              <GanttChart className="w-4 h-4 text-white" />
            </div>
            <span className="font-outfit font-semibold text-zinc-900">ProjectOS</span>
          </div>
          <p className="text-sm text-zinc-500">
            © 2025 ProjectOS. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
